import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {environment} from '../../../environments/environment';
import {Serializer, SerializerResponse} from '../interfaces/serializer';
import {SavedReport} from '../interfaces/saved-reports';
import {map, switchMap, tap} from 'rxjs/operators';
import {
  AdRequestOptions,
  AdRequestParams,
  AdRParamsCycle,
  AdRequestParamsPerformance,
  AdRPeriod,
  AdRReport,
  AdRReportType, AdRResponseRow
} from '../interfaces/ad-reports';
import {IServerSideGetRowsParams} from '@ag-grid-community/core';
import {HttpBackend, HttpClient} from '@angular/common/http';
import {ReportsTableService} from './reports-table.service';
import {LocalStorage} from "@ngx-pwa/local-storage";
import {ExportsService} from './exports.service';
import {ReportUtils} from "../libraries/report-utils";
import {Site} from '../interfaces/site';

@Injectable({
  providedIn: 'root'
})
export class ReportsRequestsService {
  public  httpWithoutInterceptor: HttpClient;

  constructor(
    private readonly _http: HttpClient,
    private readonly _adRTableS: ReportsTableService,
    private readonly _localStorage: LocalStorage,
    private readonly _exportsS: ExportsService,
    private readonly _handler: HttpBackend
  ) {
    this.httpWithoutInterceptor = new HttpClient(_handler);
  }

  public exportFile(dataset: Serializer<Site>, params: Partial<AdRequestParams>, format: string, reportData: any, separator: string): Observable<any> {
    return this._http.post(`${environment.baseUrl}/api/${dataset.id}/reports/export-file`, {
      data_set_id: dataset.id,
      format:      format,
      separator:   separator,
      name:        reportData.name || 'new_report',
      parameters:  params,
      report_type: reportData.reportType
    }, {
      observe:      'response',
      responseType: 'blob' as 'json'
    }).pipe(
      tap((respBlob: any) => {
        ReportUtils.downloadFile(respBlob);
      })
    );
  }

  public exportMail(dataset: Serializer<Site>, params: Partial<AdRequestParams>, format: string, reportId: number, emails: string, reportType: AdRReportType, name: string): Observable<any> {
    return this._http.post(`${environment.baseUrl}/api/${dataset.id}/reports/export-mail`, {
      data_set_id: dataset.id,
      name:        name || 'new_report',
      report_id:   reportId,
      format:      format,
      parameters:  params,
      emails:      emails.split(','),
      report_type: reportType
    });
  }

  public exportDestination(dataset: Serializer<Site>, params: Partial<AdRequestParams>, format: string, reportId: number, destination: Serializer<any>, reportType: AdRReportType, name: string): Observable<any> {
    return this._http.post(`${environment.baseUrl}/api/${dataset.id}/reports/export-per-destination`, {
      data_set_id:    dataset.id,
      name:           name || 'new_report',
      report_id:      reportId,
      format:         destination.relationships.data_exporter.data.attributes.slug === 'google-sheets' && this._exportsS.googleSheetFormat.value || format,
      parameters:     params,
      destination_id: destination.id,
      report_type:    reportType
    });
  }

  public updateReportStandalone(dataset: Serializer<Site>, report: Serializer<SavedReport>, update: Partial<SavedReport>): Observable<SerializerResponse<Serializer<SavedReport>>> {
    return this._http.patch(`${environment.baseUrl}/api/${dataset.id}/reports/${report.id}`, update);
  }

  public createReportStandalone(dataSetId: number, parameters: Partial<AdRequestParams>, data: Partial<SavedReport>): Observable<SerializerResponse<Serializer<SavedReport>>> {
    return this._http.post(`${environment.baseUrl}/api/${dataSetId}/reports`, {
      parameters: parameters,
      ...data
    });
  }

  public saveReport(dataset: Serializer<Site>, report: Partial<SavedReport>, params: Partial<AdRequestParams>, options: Partial<AdRequestOptions>, limit: number = null): Observable<SerializerResponse<Serializer<SavedReport>>> {
    return this._http.post(`${environment.baseUrl}/api/${dataset.id}/reports`, {
      parameters: {
        ...params,
        limit
      },
      ...report,
      real_conversion_is_enabled: options.real_conversions || false
    });
  }

  public updateReport(dataset: Serializer<Site>, report: Serializer<SavedReport>, update: Partial<SavedReport>, params: Partial<AdRequestParams>, options: Partial<AdRequestOptions>, limit: number = null): Observable<SerializerResponse<Serializer<SavedReport>>> {
    return this._http.patch(`${environment.baseUrl}/api/${dataset.id}/reports/${report.id}`, {
      parameters: {
        ...params,
        limit
      },
      ...update,
      real_conversion_is_enabled: options.real_conversions || false
    });
  }

  public getSavedReportInfos(dataset: Serializer<Site>, uuid: string, reportId: number, by: 'id' | 'uuid' = 'id'): Observable<SerializerResponse<Serializer<SavedReport>>> {
    const url: string = (by === 'id') ? `${environment.baseUrl}/api/${dataset.id}/reports/${reportId}` : `${environment.baseUrl}/api/reports/${uuid}`;
    return this._http.get<SerializerResponse<Serializer<SavedReport>>>(url);
  }

  public getAdReport(
    dataset: Serializer<Site>,
    uuid: string | null,
    token: string | null,
    params: Partial<AdRequestParams> | AdRequestParamsPerformance | AdRParamsCycle,
    options: Partial<AdRequestOptions>,
    limit: number,
    agGrid: IServerSideGetRowsParams,
    reportId: number | null,
    templateId: number | null,
  ): Observable<{ rows: any, pinnedBottomRows: any, report: AdRReport }> {
    return this.generate(
      dataset,
      uuid,
      token,
      {
        ...params,
        ...this._adRTableS.getRequestTableFilters(agGrid),
        ...this._adRTableS.getRequestTableOrders(agGrid),
        dimensions: params.dimensions,
        metrics:    params.metrics,
        limit:      limit,
        page:       agGrid.api.paginationGetCurrentPage() + 1,
        report_id:   reportId,
        template_id:   templateId,
      },
      options
    )
    .pipe(map((report: AdRReport) => {
      return this._adRTableS.initTableData(params, report);
    }));
  }

  public generate(
    dataset: Serializer<Site>,
    uuid: string | null,
    token: string | null,
    params: Partial<AdRequestParams>,
    options: Partial<AdRequestOptions> = {}
  ): Observable<AdRReport> {
    // @ts-ignore
    gtag('event', 'generate_performance',{'event_category': 'report'});

    const url: string = uuid ? `${environment.baseUrl}/api/reports/${uuid}/generate` : `${environment.baseUrl}/api/${dataset.id}/reports/generate`;
    const headers: any = {};

    if (token) {
      headers['Authorization'] = 'Bearer ' + token;
    }

    return this._http.post<AdRReport>(url, {
      parameters: params,
      options
    }, {
      headers: headers
    });
  }

  public formatTrendsData(slug: string, rows: Array<any>): Observable<Array<{ day: string, value: string, formatted_value: string }>> {
    return of(null).pipe(map(() => {
      const output: Array<{ day: string, value: string, formatted_value: string }> = [];

      for (const row of rows) {
        const rowTmp: any = {};

        rowTmp.day = row.dimensions.day.formatted_value;
        rowTmp.value = parseFloat(row.metrics[slug].value);
        rowTmp.formatted_value = row.metrics[slug].formatted_value;

        output.push(rowTmp);
      }

      return output;
    }));
  }

  private getTrendsFilters(row: AdRResponseRow): { [key: string]: Array<string> } {
    const filters: any = {};

    for (const key in row?.dimensions || {}) {
      if (!['day', 'week', 'month', 'year'].includes(key) && row.dimensions[key].value) {
        filters[key] = [row.dimensions[key].value];
      }
    }

    return filters;
  }

  public getTrends(
    dataset: Serializer<Site>,
    uuid: string | null,
    token: string | null,
    type: 'total' | 'cell',
    metric: string,
    row: AdRResponseRow,
    params: Partial<AdRequestParams>,
    options: Partial<AdRequestOptions>
  ): Observable<Array<{ day: string, value: string, formatted_value: string }>> {
    const url: string = (uuid) ? `${environment.baseUrl}/api/reports/${uuid}/generate-trends` : `${environment.baseUrl}/api/${dataset.id}/reports/generate-trends`;
    const headers: any = {};

    if (token) {
      headers['Authorization'] = token;
    }

    // @ts-ignore
    gtag('event', 'generate_trends', {'event_category': 'report'});

    return this._http.post(url, {
      type: type,
      metric: metric,
      filters: this.getTrendsFilters(row),
      parameters: params,
      options: {
        ...options,
        'generate_missing_date_chart_data': true
      }
    }).pipe(
      switchMap((report: AdRReport) => {
        return this.formatTrendsData(metric, report.rows);
      })
    );
  }

}
